// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';
import type { RequestOptions } from '@@/plugin-request/request';
import type { API } from './typings';
import { generateApiSignature } from '@/utils/apiSignature';

/**
 * Request with API signature for protected endpoints
 */
async function signedRequest<T = any>(
  url: string,
  options: RequestOptions & { method?: string; params?: Record<string, any>; data?: any } = {}
): Promise<T> {
  console.log('[signedRequest] called for:', url);
  const { method = 'GET', params, data, ...restOptions } = options;

  // Extract path from URL (remove query params)
  const urlObj = new URL(url, 'http://dummy.com');
  const path = urlObj.pathname;

  // Generate signature headers
  const signatureHeaders = await generateApiSignature(method, path, data);
  console.log('[signedRequest] signature headers:', signatureHeaders);

  const requestOptions: RequestOptions = {
    ...restOptions,
    method,
    params,
    data,
  };

  if (signatureHeaders) {
    requestOptions.headers = {
      ...requestOptions.headers,
      ...signatureHeaders,
    };
  }

  console.log('[signedRequest] final headers:', requestOptions.headers);
  return request<T>(url, requestOptions);
}

/** 获取当前的用户 GET /api/currentUser */
export async function currentUser(options?: RequestOptions) {
  return request<{
    data: API.CurrentUser;
  }>('/api/currentUser', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 退出登录接口 POST /api/login/outLogin */
export async function outLogin(options?: RequestOptions) {
  return request<Record<string, any>>('/api/login/outLogin', {
    method: 'POST',
    ...(options || {}),
  });
}

/** 登录接口 POST /admin/auth/login */
export async function login(body: API.LoginParams, options?: RequestOptions) {
  // Allow login with email or user_id. If the user typed something containing
  // "@" we treat it as an email; otherwise we send it as user_id.
  const raw = (body.identifier ?? body.email ?? '').trim();
  const looksLikeEmail = raw.includes('@');
  const loginData: Record<string, unknown> = {
    password: body.password,
  };
  if (looksLikeEmail) {
    loginData.email = raw;
  } else {
    loginData.user_id = raw;
  }

  return request<API.LoginResult>('/admin/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: loginData,
    ...(options || {}),
  });
}

/** 此处后端没有提供注释 GET /api/notices */
export async function getNotices(options?: RequestOptions) {
  return request<API.NoticeIconList>('/api/notices', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取规则列表 GET /api/rule */
export async function rule(
  params: {
    // query
    /** 当前的页码 */
    current?: number;
    /** 页面的容量 */
    pageSize?: number;
  },
  options?: RequestOptions,
) {
  return request<API.RuleList>('/api/rule', {
    method: 'GET',
    params: {
      ...params,
    },
    ...(options || {}),
  });
}

/** 更新规则 PUT /api/rule */
export async function updateRule(options?: RequestOptions) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'update',
      ...(options || {}),
    },
  });
}

/** 新建规则 POST /api/rule */
export async function addRule(options?: RequestOptions) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'post',
      ...(options || {}),
    },
  });
}

/** 删除规则 DELETE /api/rule */
export async function removeRule(options?: RequestOptions) {
  return request<Record<string, any>>('/api/rule', {
    method: 'POST',
    data: {
      method: 'delete',
      ...(options || {}),
    },
  });
}

/** 获取分类树 GET /admin/categories/tree */
export async function getCategoryTree(
  params: {
    platform?: string;
    status?: 'all' | 'active' | 'inactive';
    name?: string;
  },
  options?: RequestOptions,
) {
  return request<API.CategoryTreeResult>('/admin/categories/tree', {
    method: 'GET',
    params: {
      platform: params.platform || '1688',
      status: params.status || 'all',
      ...(params.name ? { name: params.name } : {}),
    },
    ...(options || {}),
  });
}

/** 更新分类 PUT /admin/categories/:id */
export async function updateCategory(
  id: string,
  body: {
    isActive?: boolean;
    imageUrl?: string | null;
    name?: {
      zh?: string;
      en?: string;
      ko?: string;
    };
  },
  options?: RequestOptions,
) {
  return request<API.CategoryResponse>(`/admin/categories/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新分类状态 PUT /admin/categories/:id (isActive only) */
export async function updateCategoryStatus(
  id: string,
  isActive: boolean,
  options?: RequestOptions,
) {
  return request<API.CategoryResponse>(`/admin/categories/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { isActive },
    ...(options || {}),
  });
}

/** 同级分类拖拽排序 PUT /admin/categories/reorder */
export async function reorderCategories(
  data: { platform: string; parentId: string | null; orderedIds: string[] },
  options?: RequestOptions,
) {
  return request<{ status: string; message?: string }>('/admin/categories/reorder', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data,
    ...(options || {}),
  });
}

/** 更新分类图片 PUT /admin/categories/:id (imageUrl only) */
export async function updateCategoryImage(
  id: string,
  imageUrl: string | null,
  options?: RequestOptions,
) {
  return request<API.CategoryResponse>(`/admin/categories/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { imageUrl },
    ...(options || {}),
  });
}

/** 更新分类名称 PUT /admin/categories/:id (name only) */
export async function updateCategoryName(
  id: string,
  name: {
    zh?: string;
    en?: string;
    ko?: string;
  },
  options?: RequestOptions,
) {
  return request<API.CategoryResponse>(`/admin/categories/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: { name },
    ...(options || {}),
  });
}

/** 1688 분류 갈래(뿌리→상품 분류) — 자사몰 카테고리 추천·생성에 쓴다. */
export async function getMarketplaceCategoryPath(
  categoryId: number,
  options?: RequestOptions,
) {
  return signedRequest<{
    data?: {
      categoryId: number;
      path?: {
        categoryId: number;
        level: number;
        name: { zh?: string; en?: string; ko?: string };
      }[];
    };
  }>('/admin/products/marketplace-category-path', {
    method: 'GET',
    params: { categoryId },
    ...(options || {}),
  });
}

/** 获取产品详情 GET /products/detail */
export async function getProductDetail(
  params: {
    productId: string | number;
    source?: string;
    country?: string;
  },
  options?: RequestOptions,
) {
  // Backend requires numeric offerId — coerce the incoming productId.
  const numericId = Number(params.productId);
  const offerId = Number.isFinite(numericId) ? numericId : params.productId;
  return signedRequest<API.ProductDetailResponse>('/admin/products/detail', {
    method: 'GET',
    params: {
      productId: offerId,
      offerId,
      source: params.source || '1688',
      country: params.country || 'ko',
    },
    ...(options || {}),
  });
}

/** 获取淘宝产品详情 GET /products/taobao-global/product/get?item_id=xxx */
export async function getTaobaoProductDetail(
  itemId: string,
  options?: RequestOptions,
) {
  return signedRequest<API.ProductDetailResponse>('/admin/products/taobao-global/product/get', {
    method: 'GET',
    params: {
      item_id: itemId,
    },
    ...(options || {}),
  });
}

/** 获取管理员列表 GET /admin/admins */
export async function getAdmins(
  params?: API.AdminListParams,
  options?: RequestOptions,
) {
  // Map 'current' to 'page' for API compatibility
  const apiParams: any = { ...params };
  if (apiParams.current !== undefined) {
    apiParams.page = apiParams.current;
    delete apiParams.current;
  }
  
  return request<API.AdminListResponse>('/admin/admins', {
    method: 'GET',
    params: apiParams,
    ...(options || {}),
  });
}

/** 根据ID获取管理员 GET /admin/admins/:id */
export async function getAdminById(
  id: string,
  options?: RequestOptions,
) {
  return request<API.AdminResponse>(`/admin/admins/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建管理员 POST /admin/admins */
export async function createAdmin(
  body: API.CreateAdminParams,
  options?: RequestOptions,
) {
  return request<API.AdminResponse>('/admin/admins', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新管理员 PUT /admin/admins/:id */
export async function updateAdmin(
  id: string,
  body: API.UpdateAdminParams,
  options?: RequestOptions,
) {  
  const response = await request<API.AdminResponse>(`/admin/admins/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
  
  return response;
}

/** 删除管理员 DELETE /admin/admins/:id */
export async function deleteAdmin(
  id: string,
  options?: RequestOptions,
) {
  return request<API.AdminResponse>(`/admin/admins/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 获取权限分类 GET /admin/admins/permission-categories */
export async function getPermissionCategories(
  options?: RequestOptions,
) {
  return request<{
    status: string;
    statusCode: number;
    data: {
      categories: Array<{
        id: string;
        label: string;
        permissions: Array<{
          id: string;
          label: string;
          code: string;
          description: string;
        }>;
      }>;
    };
    timestamp: string;
  }>('/admin/admins/permission-categories', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取角色权限 GET /admin/admins/roles/:role/permissions */
export async function getRolePermissions(
  role: string,
  options?: RequestOptions,
) {
  return request<{
    status: string;
    statusCode: number;
    data: {
      role: string;
      permissions: Array<{
        code: string;
        name: string;
        description: string;
      }>;
      permissionCount: number;
    };
    timestamp: string;
  }>(`/admin/admins/roles/${role}/permissions`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取角色列表 GET /admin/roles */
export async function getRoles(
  params?: API.RoleListParams,
  options?: RequestOptions,
) {
  const apiParams: any = { ...params };
  if (apiParams.current !== undefined) {
    apiParams.page = apiParams.current;
    delete apiParams.current;
  }
  
  return request<API.RoleListResponse>('/admin/roles', {
    method: 'GET',
    params: apiParams,
    ...(options || {}),
  });
}

/** 根据ID获取角色 GET /admin/roles/:id */
export async function getRoleById(
  id: string,
  options?: RequestOptions,
) {
  return request<API.RoleResponse>(`/admin/roles/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建角色 POST /admin/roles */
export async function createRole(
  body: API.CreateRoleParams,
  options?: RequestOptions,
) {
  return request<API.RoleResponse>('/admin/roles', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新角色 PUT /admin/roles/:id */
export async function updateRole(
  id: string,
  body: API.UpdateRoleParams,
  options?: RequestOptions,
) {
  return request<API.RoleResponse>(`/admin/roles/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除角色 DELETE /admin/roles/:id */
export async function deleteRole(
  id: string,
  options?: RequestOptions,
) {
  return request<API.RoleResponse>(`/admin/roles/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

